import { MRTGrid, Typography } from "@foodpilot/foods";
import { useTheme } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { createMRTColumnHelper, type MRT_PaginationState, type MRT_RowSelectionState } from "material-react-table";
import { CompanyGrid, useGetAllOrganization } from "src/api/client-api/company/useGetAllOrganization";
import { SelectionGrid, type SelectionGridColumn } from "src/components/ReferenceSearch";
import { GridText } from "src/components/Grid/GridText";
import { IngredientFormIngredientCompany } from "../../../IngredientForm.types";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { getSupplierMutationPermission } from "../supplierMutationPermission";
import { RemoteErrorAlert } from "src/components/PageState/RemoteErrorAlert";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";

type CompanyListGridProps = {
    appearance?: "legacy" | "product-v2";
    currentIngredientSuppliers: IngredientFormIngredientCompany[];
    setNewIngredientsSuppliers: (newItems: IngredientFormIngredientCompany[]) => void;
    canModify?: boolean;
    delayedSearch?: string;
    excludedSupplierIris?: ReadonlySet<IRI>;
};
export const CompanyListGrid = (props: CompanyListGridProps) => {
    return <CompanyListGridContent key={props.delayedSearch ?? ""} {...props} />;
};

const CompanyListGridContent = (props: CompanyListGridProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const {
        appearance,
        canModify = true,
        currentIngredientSuppliers,
        setNewIngredientsSuppliers,
        delayedSearch = "",
        excludedSupplierIris,
    } = props;
    const { selectedCompany } = useFoodpilotContext();
    const featureProductUxV2 = useIsProductUxV2();
    const isProductUxV2 = appearance === "product-v2" || (appearance === undefined && featureProductUxV2);

    const pageSize = isProductUxV2 ? 7 : 6;
    const [companyPage, setCompanyPage] = useState(1);

    const { companies, totalItems, isError, refetch } = useGetAllOrganization({
        page: companyPage,
        itemsPerPage: pageSize,
        type: ["branch", "main", "provider"],
        hierarchyRootId: selectedCompany?.id,
        search: delayedSearch,
    });

    const columns: SelectionGridColumn<CompanyGrid>[] = [
        {
            id: "name",
            render: (company) => (
                <Typography variant="body-bold" sx={{ color: theme.palette.primary[700] }}>
                    {company.name}
                </Typography>
            ),
        },
        {
            id: "clientId",
            render: (company) => (
                <Typography variant="body-regular" sx={{ color: theme.custom.grey[2000] }}>
                    {company.clientId ?? "-"}
                </Typography>
            ),
        },
    ];

    const repositoryError =
        isError ?
            <RemoteErrorAlert
                message={t("une_erreur_est_survenue", "An error occurred")}
                onRetry={refetch}
                status="server-error"
            />
        :   null;

    if (isError && companies === undefined) {
        return repositoryError;
    }

    const canSelectSupplier = (supplier: CompanyGrid) => {
        if (excludedSupplierIris?.has(supplier["@id"]) === true) return false;

        return getSupplierMutationPermission(canModify) === "allowed";
    };

    const toggleSupplier = (supplier: CompanyGrid) => {
        if (!canSelectSupplier(supplier)) return;

        const existingSupplier = currentIngredientSuppliers.find(
            (currentIngredientSupplier) => currentIngredientSupplier.supplier["@id"] === supplier["@id"],
        );
        if (existingSupplier !== undefined) {
            setNewIngredientsSuppliers(
                currentIngredientSuppliers.filter(
                    (currentIngredientSupplier) => currentIngredientSupplier.supplier["@id"] !== supplier["@id"],
                ),
            );
            return;
        }

        setNewIngredientsSuppliers([
            ...currentIngredientSuppliers,
            {
                supplier: {
                    "@id": supplier["@id"],
                    clientId: supplier.clientId ?? null,
                    name: supplier.name,
                },
                transportSelections: {},
                transportSelectionsStatus: "new",
            },
        ]);
    };

    if (!isProductUxV2) {
        const columnHelper = createMRTColumnHelper<CompanyGrid>();
        const legacyColumns = [
            columnHelper.accessor((row) => row.name, {
                id: "name",
                header: t("nom"),
                Cell: ({ renderedCellValue }) => <GridText value={renderedCellValue} variant="themed" />,
            }),
            columnHelper.accessor((row) => row.clientId ?? "-", {
                id: "clientId",
                header: t("code_fournisseur"),
                Cell: ({ renderedCellValue }) => <GridText value={renderedCellValue} />,
            }),
        ];
        const rowSelection: MRT_RowSelectionState = Object.fromEntries(
            (companies ?? []).map((company, index) => [
                index,
                currentIngredientSuppliers.some(
                    (currentIngredientSupplier) => currentIngredientSupplier.supplier["@id"] === company["@id"],
                ),
            ]),
        );
        const pagination: MRT_PaginationState = { pageIndex: companyPage - 1, pageSize };

        return (
            <>
                {repositoryError}
                <MRTGrid
                    columns={legacyColumns}
                    data={companies ?? []}
                    enableRowSelection={(row) => canSelectSupplier(row.original)}
                    enableSelectAll={false}
                    enablePagination
                    onRowSelectionChange={(updater) => {
                        const nextSelection = typeof updater === "function" ? updater(rowSelection) : updater;
                        const nextDraft = [...currentIngredientSuppliers];
                        let changed = false;

                        for (const [index, company] of (companies ?? []).entries()) {
                            if (!canSelectSupplier(company)) continue;

                            const isSelected = nextSelection[index] === true;
                            const existingIndex = nextDraft.findIndex(
                                (relation) => relation.supplier["@id"] === company["@id"],
                            );

                            if (isSelected && existingIndex === -1) {
                                nextDraft.push({
                                    supplier: {
                                        "@id": company["@id"],
                                        clientId: company.clientId ?? null,
                                        name: company.name,
                                    },
                                    transportSelections: {},
                                    transportSelectionsStatus: "new",
                                });
                                changed = true;
                            } else if (!isSelected && existingIndex !== -1) {
                                nextDraft.splice(existingIndex, 1);
                                changed = true;
                            }
                        }

                        if (changed) setNewIngredientsSuppliers(nextDraft);
                    }}
                    state={{
                        isLoading: companies === undefined,
                        rowSelection,
                        pagination,
                        globalFilter: delayedSearch,
                    }}
                    rowCount={totalItems ?? 0}
                    manualPagination
                    manualFiltering
                    onPaginationChange={(updater) => {
                        const nextPagination = typeof updater === "function" ? updater(pagination) : updater;
                        setCompanyPage(nextPagination.pageIndex + 1);
                    }}
                    displayColumnDefOptions={{ "mrt-row-select": { header: "" } }}
                    muiTableBodyRowProps={({ row }) =>
                        canSelectSupplier(row.original) ?
                            {}
                        :   {
                                sx: {
                                    pointerEvents: "none",
                                    td: { filter: "grayscale(1)" },
                                },
                            }
                    }
                />
            </>
        );
    }

    return (
        <>
            {repositoryError}
            <SelectionGrid
                columns={columns}
                emptyContent={
                    <Typography variant="body-regular" sx={{ color: theme.custom.grey[2000] }}>
                        {t("il_n_y_a_aucun_record_a_afficher")}
                    </Typography>
                }
                getRowId={(company) => company["@id"]}
                isLoading={companies === undefined}
                isRowDisabled={(company) => !canSelectSupplier(company)}
                isRowSelected={(company) =>
                    currentIngredientSuppliers.some(
                        (currentIngredientSupplier) => currentIngredientSupplier.supplier["@id"] === company["@id"],
                    )
                }
                onPageChange={setCompanyPage}
                onSelect={toggleSupplier}
                page={companyPage}
                pageCount={totalItems === undefined ? 0 : Math.ceil(totalItems / pageSize)}
                rows={companies ?? []}
            />
        </>
    );
};
